import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "col-md-6 offset-md-3" }
const _hoisted_2 = { class: "login-form mt-4 p-4" }
const _hoisted_3 = { class: "row g-3" }
const _hoisted_4 = { class: "col-12 status-placeholder" }
const _hoisted_5 = { class: "col-12" }
const _hoisted_6 = { class: "col-12" }
const _hoisted_7 = { class: "col-12 error-placeholder" }
const _hoisted_8 = { class: "col-12" }
const _hoisted_9 = ["disabled"]

import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import ErrorLabel from "@/components/ui/ErrorLabel.vue";
import { latestMessage, type WebSocketReceiveMessage, type WebSocketMessageEmpty, type WebSocketMessageError, type WebSocketMessageWithFields } from "@/plugins/ws";
import StatusLabel from "@/components/ui/StatusLabel.vue";
import { useToast } from "@/composables/useToast";
import {RootState} from "@/store";

type RestoreEmailProgressMessage = WebSocketMessageWithFields<'auth-restore-email-progress', {
  emailProgressMessage: string;
  emailProgressType: string;
}>;

type RestoreCancelMessage = WebSocketMessageEmpty<'auth-restore-cancel'>;
type RestoreResendMessage = WebSocketMessageEmpty<'auth-restore-resend'>;
type RestoreConfirmMessage = WebSocketMessageEmpty<'auth-restore-confirm'>;
type RestoreCodeFailMessage = WebSocketMessageError<'auth-restore-code-fail'>;

// Объединенный тип для всех сообщений
type RestoreCodeWebSocketMessageType =
    | RestoreEmailProgressMessage
    | RestoreCancelMessage
    | RestoreResendMessage
    | RestoreConfirmMessage
    | RestoreCodeFailMessage;


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'ComponentsAuthRestoreCode',
},
  __name: 'RestoreCode',
  emits: ["switchMode"],
  setup(__props, { emit: __emit }) {



const emit = __emit;

const store = useStore<RootState>();

// Типы для WebSocket сообщений компонента RestoreCode
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload: any) => void;

const code = ref('');
const emailProgress = ref('Prepare to sending ...');
const emailProgressStyle = ref('bg-info');
const latestMessageValue = ref(latestMessage);
const disabledSubmit = ref(false);

const user = computed(() => store.state.auth.user);
const restorePasswordEmail = computed(() => store.state.auth.restorePasswordEmail);

const setErrorReason = (reason: string) => store.dispatch('errorLabel/setErrorReason', reason);
const unsetErrorReason = () => store.dispatch('errorLabel/unsetErrorReason');

const { showToast } = useToast();

watch(code, () => {
  unsetErrorReason();
});

// WebSocket message handler
const handleWebSocketMessage = (data: RestoreCodeWebSocketMessageType): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'auth-restore-email-progress':
      signalEmailProgress(data.emailProgressMessage, data.emailProgressType);
      break;
    case 'auth-restore-cancel':
      signalRestoreCancel();
      break;
    case 'auth-restore-resend':
      signalRestoreResend();
      break;
    case 'auth-restore-confirm':
      signalRestoreConfirm();
      break;
    case 'auth-restore-code-fail':
      signalRestoreCodeFail(data.reason);
      break;
  }
};

watch(latestMessageValue, handleWebSocketMessage);

const handleClickActionRestoreResend = () => {
  wsSend('authRestoreResend', {});
};

const handleClickActionRestoreConfirm = () => {
  wsSend('authRestoreCode', {
    email: restorePasswordEmail.value,
    code: code.value,
  });
  disabledSubmit.value = true;
};

const handleClickActionRestoreConfirmCancel = () => {
  wsSend('authRestoreCancel', {});
};

const signalRestoreCodeFail = (payload: string) => {
  setErrorReason(payload);
  disabledSubmit.value = false;
  showToast('Ошибка подтверждения', payload, 15000);
};

const signalRestoreCancel = () => {
  unsetErrorReason();
  disabledSubmit.value = false;
  store.dispatch('auth/wsUnsetUser');
  emit('switchMode', 'restore');
};

const signalEmailProgress = (message: string | null, type: string | null) => {
  unsetErrorReason();
  emailProgress.value = message === null ? 'Prepare to sending ...' : message;
  switch (type) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
};

const signalRestoreResend = () => {
  if (user.value.restorePassword) {
    user.value.restorePassword.emailProgressMessage = null;
    user.value.restorePassword.emailProgressType = null;
  }
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
};

const signalRestoreConfirm = () => {
  unsetErrorReason();
  disabledSubmit.value = false;
  store.dispatch('auth/restoreConfirm');
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
  showToast('Почта подтверждена', 'Восстановление завершено успешно', 5000);
  emit('switchMode', 'restoreConfirm');
};

onMounted(() => {
  if (user.value.restorePassword) {
    emailProgress.value = user.value.restorePassword.emailProgressMessage === null ? 'Prepare to sending ...' : user.value.restorePassword.emailProgressMessage;
    switch (user.value.restorePassword.emailProgressType) {
      case 'error':
        emailProgressStyle.value = 'bg-danger';
        break;
      case null:
      case 'info':
      default:
        emailProgressStyle.value = 'bg-info';
        break;
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[6] || (_cache[6] = _createElementVNode("h4", null, "Подтвердите, что вы владелец восстанавливаемой почты", -1)),
        _cache[7] || (_cache[7] = _createElementVNode("p", null, "Вам на почту отправлено письмо с кодом, укажите пожалуйста тут этот код.", -1)),
        _createElementVNode("p", null, [
          _cache[2] || (_cache[2] = _createTextVNode("Если что-то пошло не так, можно ")),
          _createElementVNode("a", {
            href: "#",
            onClick: handleClickActionRestoreResend,
            id: "btn-resend"
          }, "Отправить"),
          _cache[3] || (_cache[3] = _createTextVNode(" повторно."))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(StatusLabel, {
            message: emailProgress.value,
            style: _normalizeStyle(emailProgressStyle.value)
          }, null, 8, ["message", "style"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[4] || (_cache[4] = _createElementVNode("label", null, "Почта", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "email",
            name: "email",
            class: "form-control",
            placeholder: "Email",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((restorePasswordEmail).value = $event)),
            readonly: "",
            disabled: ""
          }, null, 512), [
            [_vModelText, restorePasswordEmail.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[5] || (_cache[5] = _createElementVNode("label", null, "Код", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "text",
            name: "code",
            class: "form-control",
            placeholder: "Code",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((code).value = $event))
          }, null, 512), [
            [_vModelText, code.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(ErrorLabel)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-dark float-end ms-3",
            onClick: handleClickActionRestoreConfirm,
            disabled: disabledSubmit.value
          }, "Подтвердить", 8, _hoisted_9),
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-dark float-end",
            onClick: handleClickActionRestoreConfirmCancel
          }, "Отмена")
        ])
      ])
    ])
  ]))
}
}

})